import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { usePageMeta } from "../lib/usePageMeta";

export const OrderSuccessPage = () => {
    usePageMeta("IT'S SHOWTIME. — Order Confirmed | Showtime Carnival Co.", "Your Showtime order is confirmed.");
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [order, setOrder] = useState(null);
    const [waiting, setWaiting] = useState(true);
    const { clearCart } = useCart();

    useEffect(() => {
        if (!sessionId) {
            setWaiting(false);
            return;
        }
        let tries = 0;
        const poll = async () => {
            try {
                const res = await api.get(`/checkout/status/${sessionId}`);
                if (res.data.payment_status === "paid") {
                    setOrder(res.data);
                    setWaiting(false);
                    clearCart();
                    return;
                }
            } catch {}
            tries += 1;
            if (tries < 12) setTimeout(poll, 2000);
            else setWaiting(false);
        };
        poll();
    }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <section data-testid="order-success-page" className="min-h-[100svh] bg-ink tent-stripes text-white flex items-center">
            <div className="mx-auto max-w-2xl px-5 py-40 text-center w-full">
                {waiting ? (
                    <div className="flex flex-col items-center gap-6">
                        <Loader2 size={48} className="animate-spin text-pink" />
                        <p className="font-oswald uppercase text-2xl">Confirming your order...</p>
                    </div>
                ) : order ? (
                    <>
                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 14 }}>
                            <CheckCircle2 size={64} className="mx-auto text-pink mb-8" />
                        </motion.div>
                        <h1 className="font-oswald uppercase text-6xl md:text-8xl leading-[0.9] text-pink">It's Showtime.</h1>
                        <p className="mt-6 text-zinc-300 text-base md:text-lg">Your order is confirmed and paid. We'll be in touch with fulfillment details.</p>
                        <div className="mt-10 inline-block rounded-full border-2 border-pink px-8 py-4">
                            <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-zinc-400 mb-1">Order Number</p>
                            <p className="font-oswald text-3xl text-pink" data-testid="order-number">{order.order_number}</p>
                        </div>
                        <ul className="mt-10 max-w-sm mx-auto text-left divide-y divide-zinc-800 border-y border-zinc-800">
                            {order.items?.map((i) => (
                                <li key={i.product_id} className="py-3 flex justify-between text-sm">
                                    <span className="text-zinc-300">{i.name} × {i.qty}</span>
                                    <span className="font-bold">${(i.price * i.qty).toFixed(2)}</span>
                                </li>
                            ))}
                        </ul>
                        <Link to="/shop" data-testid="order-success-shop-link" className="mt-10 inline-block rounded-full bg-pink px-8 py-4 font-oswald uppercase tracking-widest text-ink hover:bg-white transition-colors">
                            Keep Shopping
                        </Link>
                    </>
                ) : (
                    <>
                        <h1 className="font-oswald uppercase text-4xl md:text-6xl">Hang tight.</h1>
                        <p className="mt-6 text-zinc-400">We're still confirming your payment. If you were charged, your order is safe — contact us with any questions.</p>
                        <Link to="/contact" data-testid="order-pending-contact-link" className="mt-8 inline-block rounded-full border-2 border-white px-8 py-4 font-oswald uppercase tracking-widest text-white hover:bg-white hover:text-ink transition-colors">
                            Contact Us
                        </Link>
                    </>
                )}
            </div>
        </section>
    );
};

export const OrderCancelledPage = () => {
    usePageMeta("Checkout Cancelled | Showtime Carnival Co.", "Your checkout was cancelled — your cart is still saved.");
    return (
        <section data-testid="order-cancelled-page" className="min-h-[80svh] bg-paper tent-stripes-light flex items-center">
            <div className="mx-auto max-w-xl px-5 py-40 text-center">
                <h1 className="font-oswald uppercase text-5xl md:text-6xl">Show's Not Over.</h1>
                <p className="mt-6 text-zinc-500">Checkout was cancelled. Your cart is still saved — pick up right where you left off.</p>
                <Link to="/shop" data-testid="order-cancelled-shop-link" className="mt-8 inline-block rounded-full bg-pink px-8 py-4 font-oswald uppercase tracking-widest text-ink hover:bg-ink hover:text-white transition-colors">
                    Back To Shop
                </Link>
            </div>
        </section>
    );
};
